import { useEnvApi } from '../composables/useEnvApi'
import { useServerFetch } from '../composables/useServerFetch'
import { getBrandPageUrlBatch, type BrandPageUrlItem } from '../server/common'
import { WIKIBITWEBV1 } from '../utils/constants'
import { request } from './request'

export type HomeStoreDeviceType = 1 | 2

export type HomeStoreSoftwareItem = {
  softwareId?: string
  softwareType?: number
  showPlatform?: number
  appBundleId?: string
  marketId?: number | string
  name?: string
  title?: string
  logo?: string
  downloadNum?: number | string
  activeValue?: number | string
  coverBackground?: string
  traderCode?: string
  traderName?: string
  traderIcon?: string
  rankNo?: number | string
  isShowRank?: boolean
  brandType?: number
  isExtraRank?: boolean
  screenshots?: string[] | string
}

export type HomeStoreScopedSoftwareList = {
  global?: HomeStoreSoftwareItem[]
  current?: HomeStoreSoftwareItem[]
}

export type HomeStoreStatistics = {
  applicationTotal?: number | string
  downloadTotal?: number | string
  cooperationTraderTotal?: number | string
  visitTotal?: number | string
  supportCountryTotal?: number | string
}

export type HomeStoreBannerItem = {
  id?: string
  bannerImage?: string
  marketId?: number | string | null
  appName?: string | null
  appLogo?: string | null
  appTitle?: string | null
  appBundleId?: string | null
  traderCode?: string | null
  softwareId?: string | null
  screenshots?: string[] | string | null
  downloadNum?: number | string | null
  jumpType?: number | string | null
  jumpTypeName?: string | null
  code?: string | null
  alias?: string | null
  jumpLink?: string | null
  traderName?: string | null
  traderIco?: string | null
  ad?: string | null
}

export type HomeStoreInitialPayload = {
  popularApps?: HomeStoreScopedSoftwareList
  monthlyDownloads?: HomeStoreScopedSoftwareList
  monthlyActiveApps?: HomeStoreScopedSoftwareList
  stats?: HomeStoreStatistics
  banners?: HomeStoreBannerItem[]
}

export type HomeStoreApiData = {
  popularApps: HomeStoreScopedSoftwareList | null
  monthlyDownloads: HomeStoreScopedSoftwareList | null
  banners: HomeStoreBannerItem[] | null
  statistics: HomeStoreStatistics | null
  activeApps: HomeStoreScopedSoftwareList | null
}

type HomeStoreApiResponse<T> = {
  code?: number
  data?: T
  message?: string
  succeed?: boolean
}

type HomeStoreBannerInvokePayload = {
  advertisement?: {
    apibanneradlist?: {
      data?: HomeStoreBannerItem[]
    }
  }
}

const HOME_STORE_API_PATHS = {
  popular: '/api/v1/softwate/get_popular_software',
  downloads: '/api/v2/softwate/get_software_download_ranking_list',
  statistics: '/api/v1/softwate/query_statistics_data',
  active: '/api/v1/softwate/get_month_active',
  banners: `/${WIKIBITWEBV1}/invoke-v3/v3`,
} as const

const HOME_STORE_BANNER_ROUTE = 'advertisement$apibanneradlist'

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

const isWrappedResponse = <T>(value: T | HomeStoreApiResponse<T>): value is HomeStoreApiResponse<T> => {
  return value !== null && typeof value === 'object' && 'data' in value
}

/** Normalize the raw SSR and axios response shapes to the same business payload. */
const unwrapHomeStoreResponse = <T>(response: T | HomeStoreApiResponse<T> | null): T | null => {
  if (!response) return null
  return isWrappedResponse(response) ? (response.data ?? null) : response
}

/** 兼容品牌 URL 接口的直接数组和多层 data 包装返回。 */
const normalizeBrandPageUrlItems = (value: unknown): BrandPageUrlItem[] => {
  let payload = value

  for (let depth = 0; depth < 4; depth += 1) {
    if (Array.isArray(payload)) {
      return payload.filter(
        (item): item is BrandPageUrlItem =>
          isRecord(item) && typeof item.traderCode === 'string' && typeof item.alias === 'string',
      )
    }

    if (!isRecord(payload) || !Object.prototype.hasOwnProperty.call(payload, 'data')) {
      return []
    }

    payload = payload.data
  }

  return []
}

/** 为服务商广告批量补充交易商 alias，供交易商详情页路由使用。 */
const resolveHomeStoreBannerAliases = async (banners: HomeStoreBannerItem[]) => {
  const traderCodes = [
    ...new Set(
      banners
        .filter((banner) => Number(banner.jumpType) === 15)
        .map((banner) => (typeof banner.traderCode === 'string' ? banner.traderCode.trim() : ''))
        .filter(Boolean),
    ),
  ]

  if (traderCodes.length === 0) return banners

  try {
    const response = await getBrandPageUrlBatch(traderCodes)
    const aliasMap = new Map(
      normalizeBrandPageUrlItems(response).map((item) => [item.traderCode.trim(), item.alias.trim()]),
    )

    return banners.map((banner) => {
      const traderCode = typeof banner.traderCode === 'string' ? banner.traderCode.trim() : ''
      const alias = aliasMap.get(traderCode)

      return alias ? { ...banner, alias } : banner
    })
  } catch {
    return banners
  }
}

const requestSoftwareApi = async <T>(url: string, params?: Record<string, unknown>) => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: api.v4,
    url,
    method: 'GET' as const,
    params,
  }

  const response = import.meta.server
    ? await useServerFetch<T | HomeStoreApiResponse<T>>(requestConfig)
    : await request<unknown, T | HomeStoreApiResponse<T>>(requestConfig)

  return unwrapHomeStoreResponse(response)
}

/** Fetch the country-aware popular application list. */
export const getHomeStorePopularSoftware = async (deviceType: HomeStoreDeviceType) => {
  return (await requestSoftwareApi<HomeStoreScopedSoftwareList>(HOME_STORE_API_PATHS.popular, { deviceType })) ?? {}
}

/** Fetch the monthly download ranking list. */
export const getHomeStoreDownloadRanking = async (deviceType: HomeStoreDeviceType) => {
  return (await requestSoftwareApi<HomeStoreScopedSoftwareList>(HOME_STORE_API_PATHS.downloads, { deviceType })) ?? {}
}

/** Fetch the latest WikiStore aggregate statistics. */
export const getHomeStoreStatistics = async () => {
  return (await requestSoftwareApi<HomeStoreStatistics>(HOME_STORE_API_PATHS.statistics)) ?? {}
}

/** Fetch the monthly active application ranking used when local popularity is empty. */
export const getHomeStoreMonthlyActiveRanking = async (deviceType: HomeStoreDeviceType) => {
  return (await requestSoftwareApi<HomeStoreScopedSoftwareList>(HOME_STORE_API_PATHS.active, { deviceType })) ?? {}
}

/** Fetch the homepage Store advertisement list through the existing invoke-v3 route. */
export const getHomeStoreBanners = async (deviceType: HomeStoreDeviceType) => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: import.meta.server ? api.testwikidatacenter : api.homeClient,
    url: HOME_STORE_API_PATHS.banners,
    method: 'POST' as const,
    data: {
      isWeb: true,
      platform: deviceType,
    },
    headers: {
      Routes: HOME_STORE_BANNER_ROUTE,
    },
  }

  const response = import.meta.server
    ? await useServerFetch<HomeStoreBannerInvokePayload | HomeStoreApiResponse<HomeStoreBannerInvokePayload>>(
        requestConfig,
      )
    : await request<unknown, HomeStoreBannerInvokePayload | HomeStoreApiResponse<HomeStoreBannerInvokePayload>>(
        requestConfig,
      )
  const payload = unwrapHomeStoreResponse(response)

  return await resolveHomeStoreBannerAliases(payload?.advertisement?.apibanneradlist?.data ?? [])
}

/** Load all independent Store modules without allowing one failed endpoint to hide the homepage section. */
export const getHomeStoreInitialData = async (deviceType: HomeStoreDeviceType): Promise<HomeStoreInitialPayload> => {
  const [popular, downloads, statistics, active, banners] = await Promise.allSettled([
    getHomeStorePopularSoftware(deviceType),
    getHomeStoreDownloadRanking(deviceType),
    getHomeStoreStatistics(),
    getHomeStoreMonthlyActiveRanking(deviceType),
    getHomeStoreBanners(deviceType),
  ])

  return {
    popularApps: popular.status === 'fulfilled' ? popular.value : undefined,
    monthlyDownloads: downloads.status === 'fulfilled' ? downloads.value : undefined,
    stats: statistics.status === 'fulfilled' ? statistics.value : undefined,
    monthlyActiveApps: active.status === 'fulfilled' ? active.value : undefined,
    banners: banners.status === 'fulfilled' ? banners.value : undefined,
  }
}

/** Compatibility aliases for the original API names used by the Store app branch. */
export const getHomeStorePopularApps = getHomeStorePopularSoftware
export const getHomeStoreMonthlyDownloads = getHomeStoreDownloadRanking
export const getHomeStoreActiveApps = getHomeStoreMonthlyActiveRanking

/** Return the same five responses under the original aggregate field names. */
export const getHomeStoreData = async (deviceType: HomeStoreDeviceType): Promise<HomeStoreApiData> => {
  const payload = await getHomeStoreInitialData(deviceType)

  return {
    popularApps: payload.popularApps ?? null,
    monthlyDownloads: payload.monthlyDownloads ?? null,
    banners: payload.banners ?? null,
    statistics: payload.stats ?? null,
    activeApps: payload.monthlyActiveApps ?? null,
  }
}

export { HOME_STORE_API_PATHS, HOME_STORE_BANNER_ROUTE }
